import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, message, Tag, Typography, Switch, Image } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BRAND_PRIMARY } from '../utils/brandColors';

const { Text } = Typography;

// Get API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// API calls
const specialPriceAPI = {
  getAll: (params) => axios.get(`${API_BASE_URL}/special-prices`, {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }),
  getById: (id) => axios.get(`${API_BASE_URL}/special-prices/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  })
};

const MyDiscounts = () => {
  const navigate = useNavigate();
  const [specialPrices, setSpecialPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentSpecialPrice, setCurrentSpecialPrice] = useState(null);
  const [variants, setVariants] = useState([]);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  useEffect(() => {
    fetchSpecialPrices();
  }, []);

  const fetchSpecialPrices = async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const response = await specialPriceAPI.getAll({ page, page_size: pageSize });
      
      setSpecialPrices(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        current: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      }));
    } catch (error) {
      message.error('Failed to load discount list');
      
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (record) => {
    try {
      setLoading(true);
      
      // Get complete special price record
      const response = await specialPriceAPI.getById(record.id);
      const fullRecord = response.data;
      
      setCurrentSpecialPrice(fullRecord);
      // Use variants from special price (with special prices)
      setVariants(fullRecord.variants || []);
      
      setModalVisible(true);
    } catch (error) {
      message.error('Failed to load record details');
      
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination changes
  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    fetchSpecialPrices(current, pageSize);
  };

  // Count variants
  const countVariants = (variantsData) => {
    if (!variantsData) return 0;
    if (Array.isArray(variantsData)) {
      return variantsData.length;
    }
    return 0;
  };

  const columns = [
    {
      title: 'Cover Image',
      dataIndex: 'product',
      key: 'cover_picture',
      width: 80,
      render: (product) => {
        if (!product) return '-';
        
        const coverImage = product.images?.find(img => img.id === product.cover_picture);
        const firstImage = product.images?.[0];
        const imageToShow = coverImage || firstImage;
        
        if (imageToShow) {
          return (
            <Image
              src={`${API_BASE_URL}/products/${product.id}/images/${imageToShow.id}`}
              width={40}
              height={40}
              style={{ objectFit: 'cover', borderRadius: '4px' }}
              preview={false}
            />
          );
        }
        
        return (
          <div style={{ 
            width: '40px', 
            height: '40px', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: '#999'
          }}>
            No Image
          </div>
        );
      },
    },
    {
      title: 'Product Name',
      dataIndex: ['product', 'name'],
      key: 'product_name',
      ellipsis: true,
      render: (name, record) => {
        if (!name) return '-';
        return (
          <Button 
            type="link" 
            style={{ padding: 0, height: 'auto' }}
            onClick={() => window.open(`/product/${record.product?.id}`, '_blank')}
          >
            {name}
          </Button>
        );
      },
    },
    {
      title: 'Category',
      dataIndex: ['product', 'category', 'name'],
      key: 'category',
      render: (categoryName) => categoryName || 'Uncategorized',
    },
    {
      title: 'Details',
      key: 'variants',
      render: (_, record) => {
        if (!record.variants) {
          return <Text type="secondary">-</Text>;
        }
        
        // Check if it's a simple product
        const isSimple = !record.product?.variant_types || record.product.variant_types.length === 0;
        
        if (isSimple) {
          // Simple product
          if (Array.isArray(record.variants) && record.variants.length > 0) {
            const variant = record.variants[0];
            return (
              <div>
                <div>Special Price: {variant.currency} {variant.price}</div>
                <div>Stock: {variant.stock === -1 ? 'Unlimited' : variant.stock}</div>
                <div>SKU: {variant.SKU}</div>
              </div>
            );
          } else {
            return <Text type="secondary">No variant info</Text>;
          }
        } else {
          // Multiple variants
          const variantCount = countVariants(record.variants);
          return (
            <div>
              <Tag color={BRAND_PRIMARY}>{variantCount} variants</Tag>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {(record.product?.variant_types || []).join(', ')}
              </div>
            </div>
          );
        }
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('en-US'),
      width: '15%',
    },
    {
      title: 'Action',
      key: 'action',
      width: '15%',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            View Details
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>My Discounts</h1>
      </div>

      <Table
        columns={columns}
        dataSource={specialPrices}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        size="middle"
      />

      {/* View Details Modal */}
      <Modal
        title="Discount Details"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={900}
        footer={[]}
      >
        {currentSpecialPrice && (
          <div>

            {variants.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ 
                  maxHeight: '500px', 
                  overflowY: 'auto',
                  border: '1px solid #f0f0f0',
                  borderRadius: '6px',
                  backgroundColor: '#fafafa'
                }}>
                  {variants.map((variant, index) => (
                    <div 
                      key={index}
                      style={{ 
                        padding: '16px', 
                        marginBottom: index === variants.length - 1 ? 0 : '1px',
                        backgroundColor: 'white',
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto',
                        gap: '16px',
                        alignItems: 'center'
                      }}
                    >
                      {/* SKU Image */}
                      <div style={{ width: '80px', textAlign: 'center' }}>
                        {variant.image ? (
                          <Image
                            src={`${API_BASE_URL}/products/${currentSpecialPrice.product?.id}/images/${variant.image}`}
                            width={70}
                            height={70}
                            style={{ objectFit: 'cover', borderRadius: '6px' }}
                            preview={false}
                          />
                        ) : (
                          <div style={{
                            width: '70px',
                            height: '70px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '12px'
                          }}>
                            No Image
                          </div>
                        )}
                      </div>
                      
                      {/* SKU Information (read-only) */}
                      <div>
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong style={{ color: BRAND_PRIMARY }}>SKU: </Text>
                          <Text code>{variant.SKU || '-'}</Text>
                        </div>
                        {variant.variant_path && variant.variant_path.length > 0 && (
                          <div style={{ marginBottom: '8px' }}>
                            <Text strong>Variant: </Text>
                            <Tag color={BRAND_PRIMARY}>{variant.variant_path.join(' - ')}</Tag>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          <div>
                            <Text strong>Stock: </Text>
                            <Text type="secondary">{variant.stock === -1 ? 'Unlimited' : variant.stock}</Text>
                          </div>
                          <div>
                            <Text strong>Weight: </Text>
                            <Text type="secondary">{variant.weight || 100}g</Text>
                          </div>
                        </div>
                      </div>
                      
                      {/* Special Price Display Area */}
                      <div style={{ 
                        textAlign: 'center',
                        padding: '12px',
                        backgroundColor: '#f6ffed',
                        borderRadius: '6px',
                        border: '1px solid #b7eb8f',
                        minWidth: '160px'
                      }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong style={{ color: '#52c41a' }}>Your Price</Text>
                        </div>
                        <div style={{ 
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#52c41a',
                          marginBottom: '4px'
                        }}>
                          {variant.currency} {variant.price}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyDiscounts;
